import { useState } from 'react';
import { Box, Typography, Button, Paper, Stack, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Grid } from '@mui/material';
import { Plus, Trash2, Edit } from 'lucide-react';
import { useHomework } from '@/hooks/useHomework';
import { useTeacherAssignments } from '@/hooks/useTeacherAssignments';
import { useAuth } from '@/hooks/useAuth';
import { Loader } from '@/components/feedback/Loader';
import { SubjectBadge } from '@/features/teachers/components/SubjectBadge';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import toast from 'react-hot-toast';

export function HomeworkPage() {
  const { user } = useAuth();
  const { homework, loading, createHomework, deleteHomework } = useHomework();
  const { assignments } = useTeacherAssignments(user?.teacherId);
  
  const [openAdd, setOpenAdd] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // Simple form state
  const [form, setForm] = useState({ classId: '', sectionId: '', title: '', description: '', submissionDate: '' });

  const handleAdd = async () => {
    try {
      const assignment = assignments.find(a => a.classId === form.classId && a.sectionId === form.sectionId);
      if (!assignment) throw new Error("Invalid class/section selected");
      
      await createHomework({
        ...form,
        className: assignment.className,
        sectionName: assignment.sectionName,
        subject: assignment.subject,
      });
      toast.success('Homework assigned successfully');
      setOpenAdd(false);
      setForm({ classId: '', sectionId: '', title: '', description: '', submissionDate: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to assign homework');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteHomework(deleteId);
      toast.success('Homework deleted successfully');
      setDeleteId(null);
    } catch (err) {
      toast.error('Failed to delete homework');
    }
  };

  if (loading) return <Loader />;

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Homework
          </Typography>
          <Typography color="text.secondary">
            Manage homework assignments for your classes
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={20} />} onClick={() => setOpenAdd(true)}>
          Assign Homework
        </Button>
      </Box>

      <Grid container spacing={3}>
        {homework?.length > 0 ? homework.map(hw => (
          <Grid size={{ xs: 12, md: 6 }} key={hw.id}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>{hw.title}</Typography>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hw.className} - {hw.sectionName}
                    </Typography>
                    <Typography color="text.disabled">•</Typography>
                    <SubjectBadge subject={hw.subject} />
                  </Stack>
                </Box>
                <IconButton size="small" color="error" onClick={() => setDeleteId(hw.id)}>
                  <Trash2 size={18} />
                </IconButton>
              </Stack>
              
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, mb: 2 }}>
                {hw.description}
              </Typography>
              
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: 1, borderColor: 'divider', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Assigned: {new Date(hw.createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" fontWeight={600} color={new Date(hw.submissionDate) < new Date() ? 'error.main' : 'primary.main'}>
                  Due: {new Date(hw.submissionDate).toLocaleDateString()}
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        )) : (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <Typography color="text.secondary">No homework assigned yet.</Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Add Dialog */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign New Homework</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField 
                select 
                fullWidth 
                label="Class" 
                value={form.classId} 
                onChange={(e) => setForm({...form, classId: e.target.value, sectionId: ''})}
              >
                {[...new Set(assignments?.map(a => Object.entries({id: a.classId, name: a.className})))].map(c => {
                  const [idPair, namePair] = c;
                  return <MenuItem key={idPair[1]} value={idPair[1]}>{namePair[1]}</MenuItem>
                })}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField 
                select 
                fullWidth 
                label="Section" 
                value={form.sectionId} 
                onChange={(e) => setForm({...form, sectionId: e.target.value})}
                disabled={!form.classId}
              >
                {assignments?.filter(a => a.classId === form.classId).map(s => (
                  <MenuItem key={s.sectionId} value={s.sectionId}>{s.sectionName} ({s.subject})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField 
                fullWidth 
                label="Homework Title" 
                value={form.title} 
                onChange={(e) => setForm({...form, title: e.target.value})}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField 
                fullWidth 
                label="Description" 
                multiline 
                rows={4}
                value={form.description} 
                onChange={(e) => setForm({...form, description: e.target.value})}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField 
                fullWidth 
                label="Submission Date" 
                type="date"
                slotProps={{ inputLabel: { shrink: true } }}
                value={form.submissionDate} 
                onChange={(e) => setForm({...form, submissionDate: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!form.title || !form.submissionDate || !form.sectionId}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(deleteId)}
        title="Delete Homework"
        message="Are you sure you want to delete this homework? This action cannot be undone."
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
}
